import type {
  CustomerRef,
  DeliveryNote,
  DeliveryNoteDeliveryDto,
  DeliveryNoteRequest,
} from '@/model/DeliveryNote'
import { isDeliveryNoteV2 } from '@/model/DeliveryNote'
import { hasCustomerRef } from '@/utils/customerRefHelpers'

function customerRefFromFlatRequest(
  id?: string,
  name?: string,
  vatNumber?: string,
  address?: string
): CustomerRef[] {
  if (!id?.trim() && !name?.trim()) {
    return []
  }
  return [{
    id: id?.trim() ? id : undefined,
    name: id?.trim() ? undefined : name?.trim() || undefined,
    vatNumber: id?.trim() ? undefined : vatNumber?.trim() || undefined,
    address: id?.trim() ? undefined : address?.trim() || undefined,
  }]
}

export function buildV2DeliveriesFromFlatRequest(
  request: DeliveryNoteRequest
): DeliveryNoteDeliveryDto[] {
  const payloads = request.originPayloads?.length
    ? request.originPayloads
    : (request.payloads ?? [])

  return [{
    origins: [{
      id: request.originId,
      name: request.originId ? undefined : request.originName,
    }],
    destinations: [{
      id: request.destinationId,
      name: request.destinationId ? undefined : request.destinationName,
    }],
    originCustomers: customerRefFromFlatRequest(
      request.originCustomerId,
      request.originCustomerName,
      request.originCustomerVatNumber,
      request.originCustomerAddress
    ),
    destinationCustomers: customerRefFromFlatRequest(
      request.destinationCustomerId,
      request.destinationCustomerName,
      request.destinationCustomerVatNumber,
      request.destinationCustomerAddress
    ),
    payloads,
  }]
}

function deliveryHasPerPlaceCustomers(delivery: DeliveryNoteDeliveryDto): boolean {
  const hasOrigin = (delivery.originCustomers ?? []).some(hasCustomerRef)
  const hasDestination = (delivery.destinationCustomers ?? []).some(hasCustomerRef)
  return hasOrigin || hasDestination
}

function mergeFlatCustomersIntoFirstDelivery(
  deliveries: DeliveryNoteDeliveryDto[],
  request: DeliveryNoteRequest
): DeliveryNoteDeliveryDto[] {
  if (!deliveries.length || deliveries.some(deliveryHasPerPlaceCustomers)) {
    return deliveries
  }
  const fromFlat = buildV2DeliveriesFromFlatRequest(request)[0]
  const [first, ...rest] = deliveries
  return [{
    ...first,
    originCustomers: fromFlat.originCustomers,
    destinationCustomers: fromFlat.destinationCustomers,
  }, ...rest]
}

export function applyV2DeliveriesForApiRequest(
  request: DeliveryNoteRequest,
  existingNote?: DeliveryNote,
  v2Enabled = false
): DeliveryNoteRequest {
  if (!v2Enabled) {
    if (existingNote && isDeliveryNoteV2(existingNote)) {
      return request
    }
    const { type: _type, schemaVersion: _schema, deliveries: _deliveries, ...withoutV2 } = request
    return withoutV2
  }

  if (existingNote && !isDeliveryNoteV2(existingNote)) {
    const { type: _type, schemaVersion: _schema, deliveries: _deliveries, ...withoutV2 } = request
    return withoutV2
  }

  const isV2Request = request.schemaVersion === 2
    || !!request.type
    || !!request.deliveries?.length

  if (!isV2Request) {
    return request
  }

  const deliveries = request.deliveries?.length
    ? mergeFlatCustomersIntoFirstDelivery(
      request.deliveries.map((delivery) => ({
        ...delivery,
        origins: [...delivery.origins],
        destinations: [...delivery.destinations],
        originCustomers: [...(delivery.originCustomers ?? [])],
        destinationCustomers: [...(delivery.destinationCustomers ?? [])],
        payloads: [...(delivery.payloads ?? [])],
      })),
      request
    )
    : buildV2DeliveriesFromFlatRequest(request)

  return {
    ...request,
    schemaVersion: 2,
    deliveries,
  }
}
